import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

/**
 * Render device name as a link only when a detail page exists for it.
 *
 * Linkable:
 *   - manufacture_id === 4 (PalCare devices)
 *   - manufacture_id === 1 (System devices) EXCEPT device_type_id 10, 11,
 *     56, 57 — those are virtual/hardware-only types without a profile page
 *
 * Not linkable (rendered as plain text):
 *   - manufacture_id === 10 (SIP / speakers — no per-device profile)
 *   - everything else
 */
const NON_LINKABLE_SYSTEM_DEVICE_TYPES = new Set([10, 11, 56, 57]);
const DeviceNameLink = ({ device, className }) => {
  const mid = parseInt(device.manufacture_id, 10);
  const tid = parseInt(device.device_type_id, 10);
  const isLinkable =
    mid === 4
    || (mid === 1 && !NON_LINKABLE_SYSTEM_DEVICE_TYPES.has(tid));
  if (isLinkable) {
    return (
      <Link to={`/community/devices/${mid}/${device.device_id}`} className={className || 'ap-drawer-item__link'}>
        {device.device_name || 'Unnamed Device'}
      </Link>
    );
  }
  return <>{device.device_name || 'Unnamed Device'}</>;
};

/**
 * AreaDeviceDrawer — slide-out drawer for managing device assignments on an area.
 * Shows currently assigned devices (with remove) and an available-device list for assigning.
 *
 * Unlike the resident DeviceDrawer, this one:
 *   - Shows ALL devices (including area-level devices)
 *   - Filters out devices already assigned to THIS area
 *   - Sorts unassigned first, then assigned-to-other-areas
 *   - Shows which area a device is currently assigned to
 *   - Requires confirmation before reassigning from another area
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   area: object | null,
 *   assignedDevices: Array<{ device_id: number, device_name: string, descriptions: string, manufacture_uuid: string }>,
 *   availableDevices: Array<{ device_id: number, device_name: string, descriptions: string, manufacture_uuid: string, area_id: number, area_name: string, is_active: boolean }>,
 *   onAssignDevice: (areaId: number, device: object) => Promise<[boolean, string?]>,
 *   onRemoveDevice: (areaId: number, devices: Array<{device_id: number, device_name: string}>) => Promise<[boolean, string?]>
 * }} props
 */
const AreaDeviceDrawer = ({
  isOpen,
  onClose,
  area,
  assignedDevices,
  availableDevices,
  onAssignDevice,
  onRemoveDevice,
  canManageRestrictedDevices = true,
  isRestrictedDevice,
}) => {
  // Restricted devices (System/Speaker/Server) stay visible in the Assigned tab
  // for awareness, but only role > 80 may add or remove them.
  const checkRestricted = useCallback(
    (d) => (typeof isRestrictedDevice === 'function' ? isRestrictedDevice(d) : false),
    [isRestrictedDevice]
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('assigned');
  const [confirmDevice, setConfirmDevice] = useState(null);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }
  const [busy, setBusy] = useState(false);
  const toastTimer = useRef(null);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setTab('assigned');
      setConfirmDevice(null);
      setToast(null);
      setBusy(false);
    }
  }, [isOpen]);

  // Auto-clear toast after 3 seconds
  useEffect(() => {
    if (toast) {
      toastTimer.current = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(toastTimer.current);
    }
  }, [toast]);

  // Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (confirmDevice) {
          setConfirmDevice(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, confirmDevice]);

  /**
   * Filtered & sorted available devices:
   * 1. Exclude devices already assigned to THIS area
   * 2. Apply search filter
   * 3. Sort: unassigned first, then assigned-to-other-areas
   */
  const filteredAvailable = useMemo(() => {
    const assignedIds = new Set((assignedDevices || []).map((d) => d.device_id));
    const term = searchTerm.toLowerCase();

    return (availableDevices || [])
      .filter((d) => {
        if (assignedIds.has(d.device_id)) return false;
        if (!canManageRestrictedDevices && checkRestricted(d)) return false;
        if (term) {
          return (
            (d.device_name || '').toLowerCase().includes(term) ||
            (d.manufacture_uuid || '').toLowerCase().includes(term) ||
            (d.descriptions || '').toLowerCase().includes(term) ||
            (d.area_name || '').toLowerCase().includes(term)
          );
        }
        return true;
      })
      .sort((a, b) => {
        const aAssigned = a.area_id && a.is_active ? 1 : 0;
        const bAssigned = b.area_id && b.is_active ? 1 : 0;
        if (aAssigned !== bAssigned) return aAssigned - bAssigned;
        return (a.device_name || '').localeCompare(b.device_name || '');
      });
  }, [availableDevices, assignedDevices, searchTerm, canManageRestrictedDevices, checkRestricted]);

  const handleAssign = useCallback(
    async (device) => {
      // If device is assigned to another area, require confirmation
      if (device.area_id && device.is_active) {
        setConfirmDevice(device);
        return;
      }
      if (!area) return;

      setBusy(true);
      const result = await onAssignDevice(area.area_id, area.area_name, device);
      setBusy(false);

      if (result[0]) {
        setToast({ type: 'success', message: `${device.device_name} assigned successfully` });
        setTab('assigned');
      } else {
        setToast({ type: 'error', message: result[1] || 'Failed to assign device' });
      }
    },
    [area, onAssignDevice]
  );

  const handleConfirmReassign = useCallback(async () => {
    if (!area || !confirmDevice) return;

    const deviceName = confirmDevice.device_name;
    setBusy(true);
    setConfirmDevice(null);
    // Reassign uses the same handler signature as a fresh assign:
    // (areaId, areaName, device). The previous 2-arg call was leftover from
    // before areaName was added — it caused `device` to arrive as undefined
    // and assignDeviceToArea() crashed on `device.device_id`.
    const result = await onAssignDevice(area.area_id, area.area_name, confirmDevice);
    setBusy(false);

    if (result[0]) {
      setToast({ type: 'success', message: `${deviceName} reassigned successfully` });
      setTab('assigned');
    } else {
      setToast({ type: 'error', message: result[1] || 'Failed to reassign device' });
    }
  }, [area, confirmDevice, onAssignDevice]);

  const handleCancelReassign = useCallback(() => {
    setConfirmDevice(null);
  }, []);

  const handleRemove = useCallback(
    async (device) => {
      if (!area) return;

      setBusy(true);
      const result = await onRemoveDevice(area.area_id, [{ device_id: device.device_id, device_name: device.device_name }]);
      setBusy(false);

      if (result[0]) {
        setToast({ type: 'success', message: `${device.device_name} removed` });
      } else {
        setToast({ type: 'error', message: result[1] || 'Failed to remove device' });
      }
    },
    [area, onRemoveDevice]
  );

  // The drawer wrapper is rendered unconditionally (even when `area` is null)
  // so the first open has a closed → open transition to animate. Previously
  // an early-return on `!area` meant the drawer mounted for the first time
  // already in the open state, skipping the slide-in animation. Inner content
  // that references `area.*` is guarded below.

  return (
    <>
      {/* Backdrop */}
      {isOpen && area ? (
        <div className="ap-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      ) : null}

      {/* Drawer */}
      <div
        className={`ap-drawer ap-drawer--md ${isOpen && area ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Manage Devices"
      >
        {area ? (
        <>
        {/* Header */}
        <div className="ap-drawer__header">
          <div>
            <h2 className="ap-drawer__title">Manage Devices</h2>
            <p className="ap-drawer__subtitle">{area.area_name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ap-drawer__close"
            aria-label="Close device drawer"
          >
            <svg className="ap-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toast notification */}
        {toast ? (
          <div
            className={`ap-drawer-toast ${toast.type === 'success' ? 'ap-drawer-toast--success' : 'ap-drawer-toast--error'}`}
            role="status"
            aria-live="polite"
          >
            {toast.type === 'success' ? (
              <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            )}
            <span>{toast.message}</span>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="ap-drawer-toast__close"
              aria-label="Dismiss"
            >
              <svg className="ap-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : null}

        {/* Tab bar */}
        <div className="ap-drawer-tabs">
          <button
            type="button"
            onClick={() => { setTab('assigned'); setConfirmDevice(null); }}
            className={`ap-drawer-tab ${tab === 'assigned' ? 'is-active' : ''}`}
          >
            Assigned ({(assignedDevices || []).length})
          </button>
          <button
            type="button"
            onClick={() => { setTab('available'); setConfirmDevice(null); }}
            className={`ap-drawer-tab ${tab === 'available' ? 'is-active' : ''}`}
          >
            Available ({filteredAvailable.length})
          </button>
        </div>

        {/* Loading overlay */}
        {busy ? (
          <div className="ap-drawer-busy" aria-live="polite">
            <svg className="ap-icon-md ap-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : null}

        {/* Content */}
        <div className="ap-drawer__body">
          {tab === 'assigned' ? (
            <div className="ap-drawer__body-padded">
              {(assignedDevices || []).length === 0 ? (
                <div className="ap-drawer-empty">
                  <svg className="ap-icon-xl ap-drawer-empty__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z" />
                  </svg>
                  <p className="ap-drawer-empty__text">No devices assigned</p>
                  <button
                    type="button"
                    onClick={() => setTab('available')}
                    className="ap-drawer-empty__link"
                  >
                    Assign a device
                  </button>
                </div>
              ) : (
                <div className="ap-drawer-items">
                  {(assignedDevices || []).map((device) => {
                    const restricted = checkRestricted(device);
                    const removeBlocked = restricted && !canManageRestrictedDevices;
                    return (
                      <div key={device.device_id} className="ap-drawer-item">
                        <div className="ap-drawer-item__info">
                          <div className="ap-drawer-item__name">
                            <DeviceNameLink device={device} />
                          </div>
                          <div className="ap-drawer-item__meta">
                            {device.inovonics_model_name ? <span>{device.inovonics_model_name}</span> : null}
                            {device.inovonics_model_name && device.manufacture_uuid ? <span> · </span> : null}
                            {device.manufacture_uuid ? <span>{device.manufacture_uuid}</span> : null}
                          </div>
                          {device.descriptions ? (
                            <div className="ap-drawer-item__meta">{device.descriptions}</div>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemove(device)}
                          className="ap-drawer-btn ap-drawer-btn--remove"
                          disabled={busy || removeBlocked}
                          title={removeBlocked ? 'You do not have permission to remove this device' : undefined}
                          aria-label={`Remove ${device.device_name}`}
                        >
                          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Sticky area: search + reassign confirmation */}
              <div className="ap-drawer-sticky">
                {/* Search available devices */}
                <div className="ap-drawer-search">
                  <svg className="ap-icon-md ap-drawer-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search devices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ap-drawer-search__input"
                  />
                </div>

                {/* Reassign confirmation banner */}
                {confirmDevice ? (
                  <div className="ap-reassign-confirm" role="alert">
                    <div className="ap-reassign-confirm__message">
                      <svg className="ap-reassign-confirm__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                      <div>
                        <p className="ap-reassign-confirm__title">Reassign device?</p>
                        <p className="ap-reassign-confirm__detail">
                          <strong>{confirmDevice.device_name}</strong> is currently assigned to{' '}
                          <strong>{confirmDevice.area_name}</strong>. Reassign to{' '}
                          <strong>{area.area_name}</strong>?
                        </p>
                      </div>
                    </div>
                    <div className="ap-reassign-confirm__actions">
                      <button
                        type="button"
                        onClick={handleCancelReassign}
                        className="dp-btn dp-btn--cancel dp-btn--sm"
                        disabled={busy}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmReassign}
                        className="dp-btn dp-btn--warning dp-btn--sm"
                        disabled={busy}
                      >
                        {busy ? 'Reassigning\u2026' : 'Reassign'}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Scrollable device list */}
              <div className="ap-drawer__body-padded">
                <div className="ap-drawer-items">
                  {filteredAvailable.length === 0 ? (
                    <p className="ap-drawer-no-results">No available devices found</p>
                  ) : (
                    filteredAvailable.map((device) => {
                      const isAssignedToOther = device.area_id && device.is_active;
                      return (
                        <div
                          key={device.device_id}
                          className={`ap-drawer-item ${isAssignedToOther ? 'ap-drawer-item--assigned-other' : ''}`}
                        >
                          <div className="ap-drawer-item__info">
                            <div className="ap-drawer-item__name">
                              <DeviceNameLink device={device} />
                            </div>
                            <div className="ap-drawer-item__meta">
                              {device.inovonics_model_name ? <span>{device.inovonics_model_name}</span> : null}
                              {device.inovonics_model_name && device.manufacture_uuid ? <span> · </span> : null}
                              {device.manufacture_uuid ? <span>{device.manufacture_uuid}</span> : null}
                            </div>
                            {device.descriptions ? (
                              <div className="ap-drawer-item__meta">{device.descriptions}</div>
                            ) : null}
                            {isAssignedToOther ? (
                              <div className="ap-drawer-item__assigned-to">
                                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                                </svg>
                                Assigned to {device.area_name}
                              </div>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAssign(device)}
                            className={`ap-drawer-btn ${isAssignedToOther ? 'ap-drawer-btn--reassign' : 'ap-drawer-btn--assign'}`}
                            disabled={busy}
                            aria-label={
                              isAssignedToOther
                                ? `Reassign ${device.device_name} from ${device.area_name}`
                                : `Assign ${device.device_name}`
                            }
                          >
                            {isAssignedToOther ? (
                              <>
                                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                                </svg>
                                Reassign
                              </>
                            ) : (
                              <>
                                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                                Assign
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        </>
        ) : null}
      </div>
    </>
  );
};

export default AreaDeviceDrawer;
